/** @format */

import React, { FunctionComponent, ReactElement, useEffect, useState } from 'react';
import GridItem from '../../../components/Grid/GridItem';
import { useDispatch } from 'react-redux';
import { SimCardServiceTypeCollection, SimCardServiceTypeItem } from '../../../interfaces/collections/SimCardServiceInterface';
import { ItemLoaders } from '../../../core/loaders/ItemLoaders';
import { FormGeneralHelper } from '../../../helpers/FormGeneral';
import { Checkbox, Typography } from '@material-ui/core';
import GridContainer from '../../../components/Grid/GridContainer';
import { roles, UserRoleHelper } from '../../../helpers/UserRoleHelper';
import MvnoOperationSetup from '../../../interfaces/platform/MvnoOperatorSetup';
import { SimCardServiceTypeProfileUsage } from '../../../interfaces/platform/SimCardServiceTypeProfileUsage';

type ComponentByTypeConfigurationType = {
    partnerAllowedList: Array<{ typeId: number; id: number; apiId: string }>;
    serviceType: string;
    mvnoOperatorSetup: MvnoOperationSetup;
};

type CheckBoxConfigurationType = {
    simCardServiceType: SimCardServiceTypeItem;
    typeSelected: string | null;
    mvnoOperatorSetup: MvnoOperationSetup;
};

const CheckBoxConfiguration: FunctionComponent<CheckBoxConfigurationType> = ({
    mvnoOperatorSetup,
    simCardServiceType,
    typeSelected,
}): ReactElement => {
    const dispatch = useDispatch();
    const [checked, setChecked] = useState(typeSelected !== null);
    const onCheckBoxEvent = (event: React.ChangeEvent<HTMLInputElement>): void => {
        const value = event.target.checked;
        setChecked(value);
        updateAllowedService(value);
    };

    const updateAllowedService = (isChecked: boolean): void => {
        const itemLoader = new ItemLoaders(dispatch);
        if (isChecked) {
            callApiPost(itemLoader, {
                simCardTypeId: simCardServiceType.id,
                mvnoOperatorSetupId: mvnoOperatorSetup.id,
            });
            return;
        }
        callApiDelete(itemLoader, {
            mvnoOperatorSetupId: mvnoOperatorSetup.id,
            simCardTypeId: simCardServiceType.id,
        });
    };

    const callApiPost = (itemLoader: ItemLoaders, params: any): void => {
        itemLoader.postWithCallback(
            FormGeneralHelper.getByUri(FormGeneralHelper.getPath('/sim_card_service_type_profile_usages')),
            'insert_sim_card_service_type_profile_usages',
            params,
            (data: SimCardServiceTypeProfileUsage): void => {},
            { propagateDataToCallback: true, dispatchAfterCallback: false }
        );
    };

    const callApiDelete = (itemLoader: ItemLoaders, params: any): void => {
        itemLoader.deleteCall(FormGeneralHelper.getByUri(typeSelected ?? ''), 'delete_sim_card_service_type_profile_usages', (): void => {}, {
            dispatchAfterCallback: false,
        });
    };

    return <Checkbox checked={checked} onChange={onCheckBoxEvent} />;
};

export const ComponentByTypeConfiguration: FunctionComponent<ComponentByTypeConfigurationType> = ({
    partnerAllowedList,
    serviceType,
    mvnoOperatorSetup,
}): ReactElement => {
    const dispatch = useDispatch();
    const [typeFeaturesList, setTypeFeaturesList] = useState<Array<SimCardServiceTypeItem> | null>(null);

    useEffect(() => {
        if (typeFeaturesList === null) {
            const loadItems = new ItemLoaders(dispatch);
            loadItems.getListWithCallback(
                FormGeneralHelper.getByUri(FormGeneralHelper.getPath('/sim_card_service_types')),
                'group_load_features_list',
                [
                    { name: 'pagination', value: false },
                    { name: 'serviceType', value: serviceType },
                ],
                [{ name: 'description', value: 'asc' }],
                (data: SimCardServiceTypeCollection): void => {
                    setTypeFeaturesList(data['hydra:member']);
                },
                { propagateDataToCallback: true, dispatchAfterCallback: false }
            );
        }
    }, [dispatch, typeFeaturesList, serviceType]);

    const isServiceEnabled = (typeId: number): string | null => {
        for (let i = 0; i < partnerAllowedList.length; i++) {
            if (typeId === partnerAllowedList[i].typeId) {
                return partnerAllowedList[i].apiId;
            }
        }
        return null;
    };

    return (
        <React.Fragment>
            {typeFeaturesList && typeFeaturesList.length > 0 && (
                <GridItem xs={6}>
                    <Typography variant="h5">{serviceType}</Typography>
                    <GridContainer>
                        {typeFeaturesList.map((item) => {
                            let apiId = isServiceEnabled(item.id);
                            return (
                                <React.Fragment key={'group_type_id' + item.id}>
                                    <GridItem xs={7} style={{ marginTop: '15px;' }}>
                                        ({item.mainname}) - {item.description}
                                    </GridItem>
                                    <GridItem xs={5} style={{ textAlign: 'right' }}>
                                        {UserRoleHelper.checkRole(roles.ROLE_SIM_CARD_SERVICE_PARTNER_ALLOWED_POST) && (
                                            <CheckBoxConfiguration
                                                mvnoOperatorSetup={mvnoOperatorSetup}
                                                simCardServiceType={item}
                                                typeSelected={apiId}
                                            />
                                        )}
                                    </GridItem>
                                </React.Fragment>
                            );
                        })}
                    </GridContainer>
                    <hr />
                </GridItem>
            )}
        </React.Fragment>
    );
};
